import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export const AICustomerServiceCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-950 dark:bg-gray-900">
      <Helmet>
        <title>AI-Powered Customer Service Transformation | KOBRALABS</title>
        <meta name="description" content="Discover how KOBRALABS revolutionized customer service with AI-powered chatbots, achieving 60% faster response times and 85% customer satisfaction." />
        <meta name="keywords" content="AI chatbot, customer service, natural language processing, automation, customer satisfaction" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-pink-900/20"></div>
        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                AI-Powered Customer Service
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Tech Solutions Inc achieved 60% faster response times and 85% customer satisfaction
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">AI & Automation</span>
                <span className="bg-pink-500/20 text-pink-300 px-3 py-1 rounded-full">Chatbot</span>
                <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">NLP</span>
                <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full">Customer Service</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8">Executive Summary</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Challenge</h3>
                <p className="text-gray-300">
                  Tech Solutions Inc, a software company serving 50,000+ customers, was overwhelmed 
                  with customer support requests. Their 15-person support team was struggling with 
                  response times averaging 4-6 hours, leading to poor customer satisfaction scores 
                  and high support costs.
                </p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Solution</h3>
                <p className="text-gray-300">
                  KOBRALABS developed and deployed an intelligent AI-powered chatbot system using 
                  advanced natural language processing, machine learning, and seamless integration 
                  with existing customer service platforms.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Analysis */}
      <section className="py-16 px-4 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Detailed Analysis</h2>
            
            {/* Technical Challenges */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Technical Challenges</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-400 mb-3">Support Volume Issues</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 2,000+ daily support tickets</li>
                    <li>• 4-6 hour average response time</li>
                    <li>• 15 support agents overwhelmed</li>
                    <li>• 24/7 support requirements</li>
                    <li>• High agent turnover rate</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">System Integration</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Multiple legacy systems</li>
                    <li>• Inconsistent data formats</li>
                    <li>• No unified customer view</li>
                    <li>• Limited API access</li>
                    <li>• Complex workflow processes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* AI Solution Architecture */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">AI Solution Architecture</h3>
              <div className="bg-gray-800 p-8 rounded-xl">
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-purple-400 mb-4">Natural Language Processing</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• BERT-based intent recognition</li>
                      <li>• Named entity extraction</li>
                      <li>• Sentiment analysis</li>
                      <li>• Multi-language support</li>
                      <li>• Context understanding</li>
                      <li>• Conversation flow management</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-400 mb-4">Machine Learning Models</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Classification algorithms</li>
                      <li>• Recommendation systems</li>
                      <li>• Anomaly detection</li>
                      <li>• Predictive analytics</li>
                      <li>• Continuous learning</li>
                      <li>• A/B testing framework</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-4">Integration & APIs</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• RESTful API architecture</li>
                      <li>• Webhook notifications</li>
                      <li>• Real-time data sync</li>
                      <li>• Third-party integrations</li>
                      <li>• Custom connector development</li>
                      <li>• Security & authentication</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Implementation Process */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Implementation Process</h3>
              <div className="space-y-6">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3">Phase 1: Data Analysis & Training (6 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Comprehensive analysis of existing support tickets, customer interactions, and knowledge base to train the AI models.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Historical ticket analysis (50,000+ tickets)</li>
                    <li>• Customer interaction pattern recognition</li>
                    <li>• Knowledge base optimization</li>
                    <li>• Intent classification training</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-3">Phase 2: AI Model Development (8 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Development and training of custom AI models for intent recognition, entity extraction, and response generation.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Custom BERT model fine-tuning</li>
                    <li>• Response generation algorithms</li>
                    <li>• Sentiment analysis models</li>
                    <li>• Multi-language model training</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-purple-400 mb-3">Phase 3: System Integration (6 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Integration with existing customer service platforms, CRM systems, and knowledge management tools.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Zendesk integration</li>
                    <li>• Salesforce CRM connection</li>
                    <li>• Knowledge base API integration</li>
                    <li>• Real-time data synchronization</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">Phase 4: Testing & Deployment (4 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Comprehensive testing, user acceptance testing, and gradual rollout with monitoring and optimization.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Beta testing with select customers</li>
                    <li>• Performance optimization</li>
                    <li>• Gradual rollout strategy</li>
                    <li>• Continuous monitoring setup</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Technical Specifications</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">AI/ML Stack</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• TensorFlow 2.x for model training</li>
                    <li>• Hugging Face Transformers (BERT)</li>
                    <li>• spaCy for NLP preprocessing</li>
                    <li>• Scikit-learn for classification</li>
                    <li>• Redis for caching and session management</li>
                    <li>• PostgreSQL for structured data storage</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-4">Infrastructure</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• AWS EC2 for model serving</li>
                    <li>• AWS Lambda for serverless functions</li>
                    <li>• Amazon S3 for model storage</li>
                    <li>• CloudWatch for monitoring</li>
                    <li>• Auto-scaling based on demand</li>
                    <li>• Multi-region deployment</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Results & Metrics */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Results & Metrics</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-6 rounded-xl border border-green-500/30">
                  <div className="text-3xl font-bold text-green-400 mb-2">60%</div>
                  <div className="text-gray-300">Faster Response</div>
                  <div className="text-sm text-gray-400 mt-2">Reduced from 4 hours to 1.6 hours</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-6 rounded-xl border border-blue-500/30">
                  <div className="text-3xl font-bold text-blue-400 mb-2">85%</div>
                  <div className="text-gray-300">Customer Satisfaction</div>
                  <div className="text-sm text-gray-400 mt-2">Improved from 65%</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-6 rounded-xl border border-purple-500/30">
                  <div className="text-3xl font-bold text-purple-400 mb-2">24/7</div>
                  <div className="text-gray-300">Automated Support</div>
                  <div className="text-sm text-gray-400 mt-2">Always available</div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 p-6 rounded-xl border border-orange-500/30">
                  <div className="text-3xl font-bold text-orange-400 mb-2">30%</div>
                  <div className="text-gray-300">Cost Savings</div>
                  <div className="text-sm text-gray-400 mt-2">In customer service operations</div>
                </div>
              </div>
            </div>

            {/* AI Performance Metrics */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">AI Performance Metrics</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-purple-400 mb-4">Accuracy & Performance</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 92% intent recognition accuracy</li>
                    <li>• 89% entity extraction precision</li>
                    <li>• 95% response relevance score</li>
                    <li>• &lt; 2 second response time</li>
                    <li>• 98% uptime availability</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">Learning & Improvement</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Continuous learning from interactions</li>
                    <li>• Weekly model retraining</li>
                    <li>• A/B testing for response optimization</li>
                    <li>• Feedback loop integration</li>
                    <li>• Performance trend analysis</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Business Impact */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Business Impact</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-4">Operational Benefits</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 70% reduction in manual ticket handling</li>
                    <li>• 50% decrease in support agent workload</li>
                    <li>• Improved agent job satisfaction</li>
                    <li>• Faster resolution of common issues</li>
                    <li>• Better resource allocation</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">Customer Experience</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Instant responses to common queries</li>
                    <li>• Consistent service quality</li>
                    <li>• Multi-language support</li>
                    <li>• Personalized recommendations</li>
                    <li>• Proactive issue resolution</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Future Enhancements */}
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Future Enhancements</h3>
              <div className="bg-gray-800 p-6 rounded-xl">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-purple-400 mb-3">Advanced Features</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Voice-based interactions</li>
                      <li>• Video call integration</li>
                      <li>• Predictive issue detection</li>
                      <li>• Advanced analytics dashboard</li>
                      <li>• Integration with IoT devices</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3">Scalability Plans</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Multi-tenant architecture</li>
                      <li>• Global deployment strategy</li>
                      <li>• Advanced security features</li>
                      <li>• Custom model training</li>
                      <li>• API marketplace development</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Customer Service?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Let KOBRALABS help you implement AI-powered customer service solutions that deliver 
              exceptional experiences and operational efficiency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Get Started Today
              </Link>
              <Link
                to="/case-studies"
                className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                View All Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}; 